/**
 * ContentUpdater - Handles seamless content updates without page refresh
 * Supports different field types: text, rich text, assets, components
 */

import type {
  PreviewConfig,
  FieldUpdate,
  UpdateResult,
  RichTextAST,
  RichTextFormats,
  RichTextNode,
  AssetData,
  ComponentData,
  ComponentChainLink,
  LocationData,
  JsonValue,
  RelationUpdateValue,
} from '../types';

export class ContentUpdater {
  private config: PreviewConfig;
  private updateQueue: Map<string, FieldUpdate> = new Map();
  private isDestroyed = false;

  constructor(config: PreviewConfig) {
    this.config = config;
  }

  /**
   * Update a single field with new content
   */
  async updateField(update: FieldUpdate): Promise<UpdateResult> {
    if (this.config.debug) {
      console.log('[ContentUpdater] updateField called:', {
        entryId: update.entryId,
        fieldApiId: update.fieldApiId,
        fieldType: update.fieldType,
        componentChain: update.componentChain,
      });
    }

    if (this.isDestroyed) {
      return { success: false, error: 'ContentUpdater is destroyed' };
    }

    try {
      // Debounce updates - include component chain in key for uniqueness
      const chainKey = update.componentChain ? JSON.stringify(update.componentChain) : '';
      const updateKey = `${update.entryId}:${update.fieldApiId}:${chainKey}`;
      this.updateQueue.set(updateKey, update);

      // Wait for debounce delay
      await this.delay(this.config.updateDelay || 50);

      // Check if this update is still the latest
      const latestUpdate = this.updateQueue.get(updateKey);
      if (latestUpdate !== update) {
        return { success: true }; // Superseded by newer update
      }

      // Remove from queue
      this.updateQueue.delete(updateKey);

      // Find target elements (with optional component chain filtering)
      const elements = this.findElements(update.entryId, update.fieldApiId, update.componentChain);
      if (elements.length === 0) {
        return { success: false, error: 'No matching elements found' };
      }

      // Update all matching elements
      let hasError = false;
      let lastError = '';

      for (const element of elements) {
        try {
          await this.updateElement(element, update);
        } catch (error) {
          hasError = true;
          lastError = error instanceof Error ? error.message : String(error);
          console.error('[ContentUpdater] Failed to update element:', error);
        }
      }

      if (hasError) {
        return { success: false, error: lastError };
      }

      if (this.config.debug) {
        console.log('[ContentUpdater] Updated field:', {
          entryId: update.entryId,
          fieldApiId: update.fieldApiId,
          elementsCount: elements.length,
        });
      }

      return { success: true, element: elements[0] };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('[ContentUpdater] Update failed:', error);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Destroy content updater
   */
  destroy(): void {
    this.isDestroyed = true;
    this.updateQueue.clear();
  }

  private findElements(entryId: string, fieldApiId?: string, componentChain?: ComponentChainLink[]): HTMLElement[] {
    // Build selector
    let selector = `[data-hygraph-entry-id="${entryId}"]`;
    if (fieldApiId) {
      selector += `[data-hygraph-field-api-id="${fieldApiId}"]`;
    }

    const found = document.querySelectorAll<HTMLElement>(selector);
    let elements = Array.from(found);

    if (this.config.debug) {
      console.log('[ContentUpdater] findElements:', {
        selector,
        foundCount: elements.length,
        componentChain,
      });
    }

    // Filter by component chain if provided
    if (componentChain && componentChain.length > 0) {
      const targetChainStr = JSON.stringify(componentChain);

      if (this.config.debug) {
        console.log('[ContentUpdater] Filtering by component chain:', {
          targetChainStr,
          elementsBeforeFilter: elements.length,
        });
      }

      const filtered = elements.filter((element) => {
        const elementChain = element.getAttribute('data-hygraph-component-chain');

        if (this.config.debug) {
          console.log('[ContentUpdater] Comparing chains:', {
            target: targetChainStr,
            element: elementChain,
            matches: elementChain === targetChainStr,
          });
        }

        if (!elementChain) {
          // Element has no component chain attribute
          return false;
        }
        // Compare stringified chains for exact match
        return elementChain === targetChainStr;
      });

      if (filtered.length > 0) {
        elements = filtered;
        if (this.config.debug) {
          console.log('[ContentUpdater] Using filtered elements:', {
            elementsAfterFilter: elements.length,
          });
        }
      } else {
        // Check if any elements actually have component chain attributes.
        // If they do but none matched, we have a genuine mismatch (e.g. new unsaved
        // component with a synthetic instanceId) — don't fall back to all elements
        // as that would corrupt unrelated components.
        // If no elements have chain attributes, fall back for backward compatibility
        // with frontends that don't set data-hygraph-component-chain.
        const anyHaveChain = elements.some(
          (el) => el.getAttribute('data-hygraph-component-chain') !== null
        );

        if (anyHaveChain) {
          if (this.config.debug) {
            console.log('[ContentUpdater] Component chain mismatch, skipping update:', {
              targetChain: targetChainStr,
              existingElements: elements.length,
            });
          }
          elements = [];
        } else if (this.config.debug) {
          console.log('[ContentUpdater] No elements have component chains, falling back to all elements:', {
            fallbackCount: elements.length,
          });
        }
      }
    }

    return elements;
  }

  private async updateElement(element: HTMLElement, update: FieldUpdate): Promise<void> {
    switch (update.fieldType) {
      case 'STRING':
      case 'ID':
        this.updateTextField(element, update.newValue);
        break;

      case 'RICHTEXT':
        await this.updateRichTextField(element, update.newValue);
        break;

      case 'INT':
      case 'FLOAT':
        this.updateNumberField(element, update.newValue);
        break;

      case 'BOOLEAN':
        this.updateBooleanField(element, update.newValue);
        break;

      case 'DATETIME':
      case 'DATE':
        this.updateDateField(element, update.newValue);
        break;

      case 'ASSET':
        await this.updateAssetField(element, update.newValue);
        break;

      case 'LOCATION':
        this.updateLocationField(element, update.newValue);
        break;

      case 'COLOR':
        this.updateColorField(element, update.newValue);
        break;

      case 'COMPONENT':
        await this.updateComponentField(element, update.newValue);
        break;

      case 'COMPONENT_ARRAY': {
        // Component array changes are structural (reordering, add/remove)
        // Since Studio doesn't autosave, we can't refresh from API - we need to update DOM directly
        if (this.config.debug) {
          console.log('[ContentUpdater] COMPONENT_ARRAY change detected, reordering DOM elements');
        }

        const arrayValue = update.transformedValue ?? update.newValue;
        await this.reorderComponentArray(element, arrayValue);
        break;
      }

      case 'JSON':
        this.updateJsonField(element, update.newValue);
        break;

      case 'ENUMERATION':
        this.updateTextField(element, update.newValue);
        break;

      case 'RELATION':
        this.updateRelationField(element, update.newValue);
        break;

      default:
        throw new Error('Unsupported field type');
    }
  }

  private updateTextField(element: HTMLElement, newValue: string): void {
    if (!newValue && newValue !== '') return;

    // Update text content
    if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
      (element as HTMLInputElement).value = newValue;
    } else {
      element.textContent = newValue;
    }
  }

  private async updateRichTextField(element: HTMLElement, richTextData: RichTextAST | string | RichTextFormats): Promise<void> {
    try {
      let content: string;

      // Handle multiple format types
      if (typeof richTextData === 'string') {
        // Legacy: Studio sent pre-converted string
        content = richTextData;

        if (this.shouldUseInnerHTML(element, content)) {
          element.innerHTML = content;
        } else {
          element.textContent = content;
        }
        return;
      }

      // Handle multi-format objects - select format based on element preference
      if (richTextData && typeof richTextData === 'object') {
        if ('html' in richTextData && 'markdown' in richTextData) {
          // Multi-format object - select based on element's format preference
          const formatPreference = element.getAttribute('data-hygraph-rich-text-format');

          if (this.config.debug) {
            console.log(`[ContentUpdater] Rich Text multi-format update:`, {
              formatPreference,
              availableFormats: Object.keys(richTextData)
            });
          }

          if (formatPreference === 'html' && richTextData.html) {
            content = richTextData.html;
            if (this.hasMorphdom()) {
              const tempElement = document.createElement(element.tagName);
              tempElement.innerHTML = content;
              window.morphdom?.(element, tempElement);
            } else {
              element.innerHTML = content;
            }
          } else if (formatPreference === 'markdown' && richTextData.markdown) {
            content = richTextData.markdown;
            element.textContent = content;
          } else if (formatPreference === 'text' && richTextData.text) {
            content = richTextData.text;
            element.textContent = content;
          } else {
            // Fallback to HTML if no preference or preference not available
            content = richTextData.html || this.richTextToHTML(richTextData.ast || richTextData);
            element.innerHTML = content;
          }

        } else if ('children' in richTextData) {
          // Legacy: AST format
          content = this.richTextToHTML(richTextData as RichTextAST);
          element.innerHTML = content;
        }
      }
    } catch (error) {
      console.error('[ContentUpdater] Rich text update failed:', error);
      throw error;
    }
  }

  private updateNumberField(element: HTMLElement, newValue: number): void {
    if (newValue === null || newValue === undefined) return;

    const stringValue = String(newValue);

    if (element.tagName === 'INPUT') {
      (element as HTMLInputElement).value = stringValue;
    } else {
      element.textContent = stringValue;
    }
  }

  private updateBooleanField(element: HTMLElement, newValue: boolean): void {
    if (element.tagName === 'INPUT' && (element as HTMLInputElement).type === 'checkbox') {
      (element as HTMLInputElement).checked = newValue;
    } else {
      element.textContent = String(newValue);
    }
  }

  private updateDateField(element: HTMLElement, newValue: string): void {
    if (!newValue) return;

    // Parse and format date appropriately
    const date = new Date(newValue);
    const formattedDate = date.toLocaleDateString();

    if (element.tagName === 'INPUT' && (element as HTMLInputElement).type === 'date') {
      (element as HTMLInputElement).value = newValue.split('T')[0];
    } else {
      element.textContent = formattedDate;
    }
  }

  private async updateAssetField(element: HTMLElement, asset: AssetData | AssetData[]): Promise<void> {
    if (!asset) return;

    // Handle array of assets
    if (Array.isArray(asset)) {
      if (asset.length > 0) {
        await this.updateSingleAsset(element, asset[0]);
      }
      return;
    }

    await this.updateSingleAsset(element, asset);
  }

  private async updateSingleAsset(element: HTMLElement, asset: AssetData): Promise<void> {
    if (element.tagName === 'IMG') {
      const img = element as HTMLImageElement;
      img.src = asset.url;
      if (asset.alt) img.alt = asset.alt;
      if (asset.width) img.width = asset.width;
      if (asset.height) img.height = asset.height;
    } else if (element.tagName === 'VIDEO') {
      const video = element as HTMLVideoElement;
      video.src = asset.url;
    } else if (element.tagName === 'AUDIO') {
      const audio = element as HTMLAudioElement;
      audio.src = asset.url;
    } else if (element.tagName === 'A') {
      const link = element as HTMLAnchorElement;
      link.href = asset.url;
      if (!link.textContent) {
        link.textContent = asset.fileName;
      }
    } else {
      // For other elements, create appropriate child element
      element.innerHTML = `<img src="${asset.url}" alt="${asset.alt || ''}" />`;
    }
  }

  private updateLocationField(element: HTMLElement, location: LocationData): void {
    if (!location || typeof location.latitude !== 'number' || typeof location.longitude !== 'number') {
      return;
    }

    const locationText = `${location.latitude}, ${location.longitude}`;
    element.textContent = locationText;

    // Update data attributes for map integration
    element.setAttribute('data-latitude', String(location.latitude));
    element.setAttribute('data-longitude', String(location.longitude));
  }

  private updateColorField(element: HTMLElement, color: string): void {
    if (!color) return;

    element.textContent = color;

    // Apply color as background or text color
    if (element.style) {
      element.style.backgroundColor = color;
    }
  }

  private async updateComponentField(element: HTMLElement, componentData: ComponentData): Promise<void> {
    if (!componentData) return;

    try {
      // This is a simplified component update - in a real implementation,
      // you might need framework-specific rendering logic
      const componentHtml = this.renderComponent(componentData);

      if (this.hasMorphdom()) {
        const tempElement = document.createElement(element.tagName);
        tempElement.innerHTML = componentHtml;
        window.morphdom?.(element, tempElement);
      } else {
        element.innerHTML = componentHtml;
      }
    } catch (error) {
      console.error('[ContentUpdater] Component update failed:', error);
      throw error;
    }
  }


  private updateJsonField(element: HTMLElement, jsonData: JsonValue): void {
    if (jsonData === null || jsonData === undefined) return;

    const jsonString = JSON.stringify(jsonData, null, 2);
    element.textContent = jsonString;
  }

  private updateRelationField(element: HTMLElement, relation: RelationUpdateValue): void {
    if (relation === null) {
      element.textContent = '';
      return;
    }

    if (Array.isArray(relation)) {
      element.textContent = relation.join(', ');
      return;
    }

    element.textContent = relation;
  }

  private richTextToHTML(richTextAST: RichTextAST): string {
    if (!richTextAST.children) return '';

    return richTextAST.children.map(node => this.renderRichTextNode(node)).join('');
  }

  private renderRichTextNode(node: RichTextNode): string {
    if (typeof node.text === 'string') {
      return this.escapeHtml(node.text);
    }

    const children =
      Array.isArray(node.children)
        ? node.children.map((child) => this.renderRichTextNode(child)).join('')
        : '';

    switch (node.type) {
      case 'paragraph':
        return `<p>${children}</p>`;
      case 'heading-one':
        return `<h1>${children}</h1>`;
      case 'heading-two':
        return `<h2>${children}</h2>`;
      case 'heading-three':
        return `<h3>${children}</h3>`;
      case 'heading-four':
        return `<h4>${children}</h4>`;
      case 'heading-five':
        return `<h5>${children}</h5>`;
      case 'heading-six':
        return `<h6>${children}</h6>`;
      case 'block-quote':
        return `<blockquote>${children}</blockquote>`;
      case 'bulleted-list':
        return `<ul>${children}</ul>`;
      case 'numbered-list':
        return `<ol>${children}</ol>`;
      case 'list-item':
        return `<li>${children}</li>`;
      case 'link': {
        const href = typeof node.href === 'string' ? node.href : '';
        return `<a href="${href}">${children}</a>`;
      }
      case 'bold':
        return `<strong>${children}</strong>`;
      case 'italic':
        return `<em>${children}</em>`;
      case 'underline':
        return `<u>${children}</u>`;
      case 'code':
        return `<code>${children}</code>`;
      default:
        return children;
    }
  }

  /**
   * Reorder component array by moving existing DOM elements
   * This preserves the React-rendered HTML structure and styling
   */
  private async reorderComponentArray(container: HTMLElement, components: ComponentData[]): Promise<void> {
    if (!Array.isArray(components)) {
      console.warn('[ContentUpdater] Invalid components array');
      return;
    }

    // Handle empty array - clear the container
    if (components.length === 0) {
      if (this.config.debug) {
        console.log('[ContentUpdater] Empty component array, clearing container');
      }
      container.innerHTML = '';
      return;
    }

    if (this.config.debug) {
      console.log('[ContentUpdater] Reordering', components.length, 'components');
    }

    // Get all direct children that look like React-rendered components
    const existingElements = Array.from(container.children) as HTMLElement[];

    // Create a map of component ID to DOM element
    const elementMap = new Map<string, HTMLElement>();

    existingElements.forEach(el => {
      // Extract component instance ID from data-hygraph-component-chain attribute
      // Format: [{"fieldApiId":"content","instanceId":"cm123abc"}]
      const componentChainAttr = el.getAttribute('data-hygraph-component-chain');

      if (componentChainAttr) {
        try {
          const componentChain = JSON.parse(componentChainAttr);
          if (Array.isArray(componentChain) && componentChain.length > 0) {
            const instanceId = componentChain[componentChain.length - 1]?.instanceId;
            if (instanceId) {
              elementMap.set(instanceId, el);
              if (this.config.debug) {
                console.log('[ContentUpdater] Mapped component instance', instanceId, 'to DOM element');
              }
            }
          }
        } catch (e) {
          console.warn('[ContentUpdater] Failed to parse component chain:', componentChainAttr);
        }
      }
    });

    // Reorder elements based on the new component order
    const fragment = document.createDocumentFragment();
    let reorderedCount = 0;

    components.forEach((component, index) => {
      const componentId = component.id;

      if (!componentId) {
        console.warn('[ContentUpdater] Component missing ID at index', index);
        return;
      }

      const element = elementMap.get(componentId);

      if (element) {
        // Move existing element to fragment (in new order)
        fragment.appendChild(element);
        reorderedCount++;
      } else if (this.config.debug) {
        console.log('[ContentUpdater] New component not in DOM, skipping:', componentId);
      }
    });

    // Replace all children with reordered elements
    if (reorderedCount > 0) {
      container.innerHTML = ''; // Clear container
      container.appendChild(fragment); // Add reordered elements

      if (this.config.debug) {
        console.log('[ContentUpdater] Successfully reordered', reorderedCount, 'elements');
      }
    } else {
      console.warn('[ContentUpdater] Could not match any components to DOM elements');
    }

    // Log skipped new components (they'll appear after save + refresh)
    const unmatchedCount = components.length - reorderedCount;
    if (unmatchedCount > 0 && this.config.debug) {
      console.log('[ContentUpdater]', unmatchedCount, 'new component(s) not in DOM — will appear after save');
    }
  }

  private renderComponent(componentData: ComponentData): string {
    // Generic component rendering - not used for COMPONENT_ARRAY anymore (we reorder instead)
    // Kept for backward compatibility with COMPONENT field type
    const typename = componentData.__typename;
    const fields = Object.entries(componentData)
      .filter(([key]) => key !== '__typename' && key !== 'id')
      .map(([key, value]) => {
        const formatted = this.formatComponentValue(value);
        // Check if the formatted value is HTML (contains tags) - if so, don't escape
        const isHTML = formatted.includes('<') && formatted.includes('>');
        return `<div data-field="${key}">${isHTML ? formatted : this.escapeHtml(formatted)}</div>`;
      })
      .join('');

    return `<div data-component="${typename}">${fields}</div>`;
  }

  private formatComponentValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    // Check if this is a RichText multi-format object
    if (this.isRichTextFormats(value)) {
      // Use HTML format for rendering
      return value.html || '';
    }

    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  /**
   * Check if a value is a RichText multi-format object
   * These objects have html, markdown, text, and ast properties
   */
  private isRichTextFormats(value: unknown): value is { html: string; markdown: string; text: string; ast: unknown } {
    return (
      value !== null &&
      typeof value === 'object' &&
      'html' in value &&
      'markdown' in value &&
      'text' in value &&
      'ast' in value
    );
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private hasMorphdom(): boolean {
    return typeof window.morphdom === 'function';
  }

  private shouldUseInnerHTML(_element: HTMLElement, content: string): boolean {
    // Use innerHTML if content contains HTML tags
    if (content.includes('<') && content.includes('>')) {
      return true;
    }

    // Otherwise use textContent
    return false;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
